import { useRef, useMemo, useEffect, useCallback, Suspense } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { useDisposableGLTF } from '@/hooks/useDisposableGLTF'
import { PUZZLE_ASSEMBLY } from '@/lib/constants'
import { MAP_SAGE, MAP_LAKE } from '@/lib/mapColors'
import { easeOutCubic, easeInOutQuad } from '@/lib/easing'
import { mulberry32 } from '@/lib/seededRng'

const MAX_BANDS = 8
const DROP_HEIGHT_MULT = 1.5
const PIECE_DURATION = PUZZLE_ASSEMBLY.pieceDuration
const PIECE_END = PUZZLE_ASSEMBLY.dropProgressEnd
const CONTOUR_INTERVAL = 6
const PUZZLE_TERRAIN_COLOR = 0xf5f5f5
const PUZZLE_TRAIL_COLOR = MAP_SAGE
const PUZZLE_LAKE_COLOR = MAP_LAKE

interface PuzzleAssemblyProps {
  modelUrl: string
  scrollProgressRef: React.MutableRefObject<number>
  /**
   * 'horizontal' (default): camera reveals from right-half to full-width as
   * scroll progresses — used on ≥sm where steps sit in a left column.
   * 'full': camera always uses full-width — used on mobile where the canvas
   * is laid out via CSS (top half → full) and the camera should not also
   * apply a horizontal squeeze.
   */
  splitMode?: 'horizontal' | 'full'
}

/**
 * MeshPhongMaterial with onBeforeCompile for band clipping + contour lines.
 * Bands are set to full range so all geometry is visible (no clipping).
 */
function createPuzzleMaterial({
  color,
  contourInterval = 0,
  contourColor = new THREE.Color(0xffffff),
  contourWidth = 1.5,
  enableContours = true,
  zMin,
  zMax,
}: {
  color: number | string
  contourInterval?: number
  contourColor?: THREE.Color
  contourWidth?: number
  enableContours?: boolean
  zMin: number
  zMax: number
}) {
  const uniforms = {
    uBandTops: { value: new Float32Array(MAX_BANDS) },
    uBandBottoms: { value: new Float32Array(MAX_BANDS) },
    uNumBands: { value: 1 },
    uContourInterval: { value: contourInterval },
    uContourColor: { value: contourColor },
    uContourWidth: { value: contourWidth },
    uEnableContours: { value: enableContours ? 1.0 : 0.0 },
  }

  // Set bands to cover full Z range (no clipping)
  uniforms.uBandTops.value[0] = zMax + 10000
  uniforms.uBandBottoms.value[0] = zMin - 10000

  const material = new THREE.MeshPhongMaterial({
    color,
    shininess: 5,
    specular: new THREE.Color(0x111111),
    flatShading: false,
    transparent: true,
    opacity: 0,
  })

  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms)

    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      `#include <common>
varying float vWorldZ;`,
    )
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
vWorldZ = (modelMatrix * vec4(position, 1.0)).z;`,
    )

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>
varying float vWorldZ;
uniform float uBandTops[${MAX_BANDS}];
uniform float uBandBottoms[${MAX_BANDS}];
uniform int uNumBands;
uniform float uContourInterval;
uniform vec3 uContourColor;
uniform float uContourWidth;
uniform float uEnableContours;`,
    )

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <dithering_fragment>',
      `#include <dithering_fragment>

bool insideBand = false;
for (int i = 0; i < ${MAX_BANDS}; i++) {
  if (i >= uNumBands) break;
  if (vWorldZ >= uBandBottoms[i] && vWorldZ <= uBandTops[i]) {
    insideBand = true;
    break;
  }
}
if (!insideBand) discard;

if (uEnableContours > 0.5) {
  float elevation = vWorldZ;
  float line = abs(fract(elevation / uContourInterval + 0.5) - 0.5);
  float fw = fwidth(elevation / uContourInterval);
  float contour = 1.0 - smoothstep(fw * uContourWidth * 0.5, fw * uContourWidth * 1.5, line);
  gl_FragColor.rgb = mix(gl_FragColor.rgb, uContourColor, contour * 0.35);
}`,
    )
  }

  material.customProgramCacheKey = () => `puzzle:${enableContours ? '1' : '0'}`
  material.userData.uniforms = uniforms
  return material
}

interface CellGroup {
  group: THREE.Group
  meshes: THREE.Mesh[]
  materials: THREE.Material[]
  staggerIndex: number
  lastOpacity: number
}

function PuzzleAssemblyScene({
  modelUrl,
  scrollProgressRef,
  splitMode,
}: {
  modelUrl: string
  scrollProgressRef: React.MutableRefObject<number>
  splitMode: 'horizontal' | 'full'
}) {
  const { scene } = useDisposableGLTF(modelUrl)
  const { camera, size, scene: threeScene } = useThree()
  const cellGroupsRef = useRef<CellGroup[]>([])

  // Clone once per source scene and compute vertex normals at clone time so we
  // don't re-clone / recompute normals on every cache-key rebuild. The bbox is
  // derived from this same clone.
  const cloned = useMemo(() => {
    const c = scene.clone(true)
    c.traverse((child) => {
      if (child instanceof THREE.Mesh) child.geometry.computeVertexNormals()
    })
    return c
  }, [scene])

  const { zMin, zMax, zRange, extentX, extentY, center, screenX, screenY, orbitRadius } =
    useMemo(() => {
      // Compute bounding box
      const box = new THREE.Box3()
      cloned.traverse((child) => {
        if (child instanceof THREE.Mesh) box.expandByObject(child)
      })

      const min = box.min
      const max = box.max
      const zMinVal = min.z
      const zMaxVal = max.z
      const zRangeVal = zMaxVal - zMinVal
      const extX = max.x - min.x
      const extY = max.y - min.y
      const c = new THREE.Vector3((min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2)

      const rot = extY > extX
      const sX = (rot ? extY : extX) * 0.5
      const sY = (rot ? extX : extY) * 0.5
      const viewScale = Math.max(extX, extY) * 0.55
      const oRadius = viewScale * 2

      return {
        zMin: zMinVal,
        zMax: zMaxVal,
        zRange: zRangeVal,
        extentX: extX,
        extentY: extY,
        center: c,
        screenX: sX,
        screenY: sY,
        rotated: rot,
        orbitRadius: oRadius,
      }
    }, [cloned])

  // Build cell groups from meshes — matching topo-reveal exactly
  useEffect(() => {
    const meshes: THREE.Mesh[] = []
    cloned.traverse((child) => {
      if (child instanceof THREE.Mesh) meshes.push(child)
    })

    const cellMap = new Map<string, THREE.Group>()
    const nameRe = /^(terrain|trail|lake)(?:_\d+)?_r(\d+)_c(\d+)/

    for (const child of meshes) {
      const m = (child.name || '').match(nameRe)
      if (!m) continue

      const key = `r${m[2]}_c${m[3]}`
      if (!cellMap.has(key)) {
        const group = new THREE.Group()
        group.userData.key = key
        cellMap.set(key, group)
      }

      const pieceType = m[1]
      const isOverlay = pieceType === 'trail' || pieceType === 'lake'
      const color =
        pieceType === 'trail'
          ? PUZZLE_TRAIL_COLOR
          : pieceType === 'lake'
            ? PUZZLE_LAKE_COLOR
            : PUZZLE_TERRAIN_COLOR
      const mat = createPuzzleMaterial({
        color,
        contourInterval: isOverlay ? 0 : CONTOUR_INTERVAL,
        enableContours: !isOverlay,
        zMin,
        zMax,
      })

      child.material = mat
      cellMap.get(key)!.add(child)
    }

    // Seeded shuffle — matching topo-reveal's Fisher-Yates
    const rng = mulberry32(PUZZLE_ASSEMBLY.seed)
    const groups = [...cellMap.values()]
    for (let i = groups.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1))
      ;[groups[i], groups[j]] = [groups[j], groups[i]]
    }

    // Initial state: elevated and invisible. Cache each cell's flat mesh/material
    // arrays so useFrame can set opacity directly without a per-frame traverse.
    const dropHeight = zRange * DROP_HEIGHT_MULT
    const cells: CellGroup[] = groups.map((group, idx) => {
      group.position.z = dropHeight
      const cellMeshes: THREE.Mesh[] = []
      const cellMaterials: THREE.Material[] = []
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.material.opacity = 0
          cellMeshes.push(child)
          cellMaterials.push(child.material as THREE.Material)
        }
      })
      threeScene.add(group)
      return {
        group,
        meshes: cellMeshes,
        materials: cellMaterials,
        staggerIndex: idx,
        lastOpacity: 0,
      }
    })
    cellGroupsRef.current = cells

    return () => {
      cells.forEach(({ group, materials }) => {
        threeScene.remove(group)
        // Dispose only the materials we created here. Geometry is shared with
        // the GLTF cache (scene.clone keeps geometry by reference), so disposing
        // it would corrupt the cache for other consumers / remounts.
        materials.forEach((m) => m.dispose())
      })
      cellGroupsRef.current = []
    }
  }, [cloned, threeScene, zMin, zMax, zRange])

  // Lights — matching topo-reveal exactly (1.3, 0.1, 0.3 + ambient 1.3)
  useEffect(() => {
    const cx = center.x
    const cy = center.y
    const cz = center.z

    // three r155+ physically-correct lighting scales diffuse by 1/π, so
    // AmbientLight(1.0) only lifts a surface to ~0.32× its color and the
    // painted terrain read too dark. 1.8 lifts flat faces most of the way back
    // toward their palette color, matching ModelViewer.
    const ambient = new THREE.AmbientLight(0xffffff, 1.8)

    const l1 = new THREE.DirectionalLight(0xffffff, 1.0)
    l1.position.set(cx + extentX * 2, cy - extentY * 1.5, zMin)
    l1.target.position.set(cx, cy, cz)

    const l2 = new THREE.DirectionalLight(0xffffff, 0.1)
    l2.position.set(cx - extentX * 1.5, cy + extentY, zMin)
    l2.target.position.set(cx, cy, cz)

    const l3 = new THREE.DirectionalLight(0xffffff, 0.25)
    l3.position.set(cx, cy, zMax + zRange * 4)
    l3.target.position.set(cx, cy, cz)

    threeScene.add(ambient, l1, l1.target, l2, l2.target, l3, l3.target)

    return () => {
      threeScene.remove(ambient, l1, l1.target, l2, l2.target, l3, l3.target)
      ambient.dispose()
      l1.dispose()
      l2.dispose()
      l3.dispose()
    }
  }, [threeScene, center, extentX, extentY, zMin, zMax, zRange])

  // Camera frustum — contain-fit with 1.3x margin for orbit.
  //   horizontal: reveal grows from right-half to full-width as layoutT 0→1
  //     (used on desktop where steps sit in a left column and the model
  //     occupies the right side, then expands to full).
  //   full: always use full-width — used on mobile, where the canvas is laid
  //     out via CSS (top portion → full) so the camera should not also
  //     apply a horizontal squeeze.
  const containFit = useCallback(
    (w: number, h: number, layoutT: number) => {
      if (w === 0 || h === 0 || screenX <= 1) return
      const cam = camera as THREE.OrthographicCamera
      const scale = 1.3
      const sx = screenX * scale
      const sy = screenY * scale

      const P = splitMode === 'horizontal' ? 0.5 + 0.5 * layoutT : 1
      const wEffective = w * P
      const containerAspect = wEffective / h
      const terrainAspect = sx / sy
      let halfW: number
      let halfH: number
      if (containerAspect > terrainAspect) {
        halfH = sy
        halfW = halfH * containerAspect
      } else {
        halfW = sx
        halfH = halfW / containerAspect
      }

      cam.top = halfH
      cam.bottom = -halfH
      cam.right = halfW
      cam.left = halfW - (2 * halfW) / P
      cam.updateProjectionMatrix()
    },
    [camera, screenX, screenY, splitMode],
  )

  // Initial camera setup
  useEffect(() => {
    const cam = camera as THREE.OrthographicCamera
    cam.near = 0.1
    cam.far = zRange * 20
    cam.up.set(0, 0, 1)

    const angle = PUZZLE_ASSEMBLY.cameraOrbitStart
    const camX = center.x + Math.cos(angle) * orbitRadius
    const camY = center.y + Math.sin(angle) * orbitRadius
    const camZ = zMax + orbitRadius * 0.6
    cam.position.set(camX, camY, camZ)
    cam.lookAt(center)

    const p = Math.max(0, Math.min(1, scrollProgressRef.current))
    const lt = Math.max(0, Math.min(1, (p - 0.5) / 0.15))
    containFit(size.width, size.height, lt)
  }, [
    camera,
    center,
    zMax,
    zRange,
    orbitRadius,
    containFit,
    size.width,
    size.height,
    scrollProgressRef,
  ])

  useEffect(() => {
    const p = Math.max(0, Math.min(1, scrollProgressRef.current))
    const lt = Math.max(0, Math.min(1, (p - 0.5) / 0.15))
    containFit(size.width, size.height, lt)
  }, [size.width, size.height, containFit, scrollProgressRef])

  // Animation — matching topo-reveal updatePuzzleAnimation
  useFrame(() => {
    const cells = cellGroupsRef.current
    const N = cells.length
    if (N === 0) return

    const progress = Math.max(0, Math.min(1, scrollProgressRef.current))
    const layoutT = Math.max(0, Math.min(1, (progress - 0.5) / 0.15))
    const dropHeight = zRange * DROP_HEIGHT_MULT
    const staggerGap = (PIECE_END - PIECE_DURATION) / Math.max(N - 1, 1)

    for (let i = 0; i < N; i++) {
      const cell = cells[i]
      const startT = i * staggerGap
      const localT = Math.max(0, Math.min(1, (progress - startT) / PIECE_DURATION))

      // Z offset: drop from above
      const eased = easeOutCubic(localT)
      cell.group.position.z = dropHeight * (1 - eased)

      // Opacity: fade in during first 20%. Set material.opacity directly on the
      // cached flat array, skipping cells whose opacity hasn't changed.
      const opacity = Math.min(1, localT / 0.2)
      if (opacity !== cell.lastOpacity) {
        const mats = cell.materials
        for (let j = 0; j < mats.length; j++) {
          mats[j].opacity = opacity
        }
        cell.lastOpacity = opacity
      }
    }

    // Camera orbit in XY plane, looking down from above
    const cam = camera as THREE.OrthographicCamera
    const angle =
      PUZZLE_ASSEMBLY.cameraOrbitStart +
      (PUZZLE_ASSEMBLY.cameraOrbitEnd - PUZZLE_ASSEMBLY.cameraOrbitStart) * easeInOutQuad(progress)
    const camX = center.x + Math.cos(angle) * orbitRadius
    const camY = center.y + Math.sin(angle) * orbitRadius
    const camZ = zMax + orbitRadius * 0.6
    cam.position.set(camX, camY, camZ)
    cam.lookAt(center)
    containFit(size.width, size.height, layoutT)
  })

  return null
}

export function PuzzleAssembly({
  modelUrl,
  scrollProgressRef,
  splitMode = 'horizontal',
}: PuzzleAssemblyProps) {
  return (
    <Canvas
      orthographic
      flat
      camera={{
        near: 0.1,
        far: 10000,
        position: [0, 0, 100],
      }}
      gl={{ toneMapping: THREE.NoToneMapping }}
      style={{ background: 'transparent', width: '100%', height: '100%' }}
      dpr={[1, 2]}
    >
      <Suspense fallback={null}>
        <PuzzleAssemblyScene
          modelUrl={modelUrl}
          scrollProgressRef={scrollProgressRef}
          splitMode={splitMode}
        />
      </Suspense>
    </Canvas>
  )
}
